import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import {
  CheckCircle2, Circle, ChevronDown, ChevronUp, X, Rocket, PlayCircle,
} from 'lucide-react';
import {
  isChecklistDismissed, setChecklistDismissed, isFieldViewReviewed,
} from '../../lib/tutorial';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// label, plain-English helper, target route, completion key
const ITEMS = [
  { key: 'profileComplete', label: 'Complete your profile', to: '/onboarding',
    hint: 'Your business name, phone, and area so clients can find you.' },
  { key: 'servicesAdded', label: 'Add your services', to: '/dashboard/services',
    hint: 'List what you do and your prices.' },
  { key: 'availabilitySet', label: 'Set your availability', to: '/dashboard/availability',
    hint: 'The days and hours you work.' },
  { key: 'routeZonesSet', label: 'Set route days (optional)', to: '/dashboard/route-zones',
    hint: 'Group jobs by area to cut down on driving.', optional: true },
  { key: 'invoiceSettingsAdded', label: 'Add your invoice info', to: '/dashboard/invoice-settings',
    hint: 'Business name and contact details that show on invoices.' },
  { key: 'logoUploaded', label: 'Upload your logo', to: '/dashboard/invoice-settings',
    hint: 'Makes your invoices look professional.' },
  { key: 'paymentInstructionsAdded', label: 'Add payment instructions', to: '/dashboard/invoice-settings',
    hint: 'How clients pay you — Venmo, check, etc.' },
  { key: 'fieldViewReviewed', label: 'Look at your Today screen', to: '/dashboard/field',
    hint: 'Your work list for the day.' },
  { key: 'firstBookingCreated', label: 'Add or review a booking', to: '/dashboard/bookings',
    hint: 'Bookings show up here and on your Today screen.' },
  { key: 'firstInvoiceCreated', label: 'Create your first invoice', to: '/dashboard/field',
    hint: 'Turn a finished job into a bill from the Today screen.' },
];

export default function GettingStartedChecklist({ onStartTour }) {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState(null);
  const [collapsed, setCollapsed] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(isChecklistDismissed(user?.id));

  React.useEffect(() => {
    let active = true;
    axios
      .get(`${API_URL}/api/dashboard/getting-started`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (active) setStatus(res.data);
      })
      .catch(() => { /* non-blocking */ });
    return () => { active = false; };
  }, [token]);

  if (dismissed || !status) return null;

  const done = (key) =>
    key === 'fieldViewReviewed' ? isFieldViewReviewed(user?.id) : !!status[key];

  const required = ITEMS.filter((it) => !it.optional);
  const completeCount = required.filter((it) => done(it.key)).length;
  const pct = Math.round((completeCount / required.length) * 100);
  const allDone = completeCount === required.length;

  const handleDismiss = () => {
    setChecklistDismissed(user?.id, true);
    setDismissed(true);
  };

  return (
    <Card className="border-primary/30 bg-primary/[0.03]" data-testid="getting-started-checklist">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Rocket className="h-5 w-5 text-primary flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold">
                {allDone ? "You're all set up!" : 'Getting Started'}
              </p>
              <p className="text-xs text-muted-foreground">
                {allDone
                  ? 'Nice work — your account is ready to go.'
                  : `${completeCount} of ${required.length} steps done`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              variant="ghost" size="sm" className="h-8 w-8 p-0"
              onClick={() => setCollapsed((c) => !c)}
              data-testid="checklist-collapse-btn"
              aria-label={collapsed ? 'Expand' : 'Collapse'}
            >
              {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost" size="sm" className="h-8 w-8 p-0"
              onClick={handleDismiss}
              data-testid="checklist-dismiss-btn"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="mt-3">
          <Progress value={pct} className="h-2" data-testid="checklist-progress" />
        </div>

        {!collapsed && (
          <>
            <ul className="mt-3 space-y-1" data-testid="checklist-items">
              {ITEMS.map((it) => {
                const isDone = done(it.key);
                return (
                  <li key={it.key}>
                    <button
                      type="button"
                      onClick={() => navigate(it.to)}
                      className="w-full flex items-start gap-2 rounded-md p-2 text-left hover:bg-muted/60 transition"
                      data-testid={`checklist-item-${it.key}`}
                    >
                      {isDone ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" data-testid={`checklist-done-${it.key}`} />
                      ) : (
                        <Circle className="h-5 w-5 text-muted-foreground/50 flex-shrink-0 mt-0.5" />
                      )}
                      <span className="min-w-0">
                        <span className={`text-sm font-medium ${isDone ? 'text-muted-foreground line-through' : ''}`}>
                          {it.label}
                        </span>
                        {!isDone && <span className="block text-xs text-muted-foreground">{it.hint}</span>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="mt-2 pt-3 border-t">
              <Button variant="outline" size="sm" onClick={onStartTour} data-testid="checklist-start-tour-btn">
                <PlayCircle className="h-4 w-4 mr-1" /> Take the quick tour
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
